/**
 * The "expanded detail" card for a single node — image, full title,
 * description(s), quantity, total duration (optional), and tool chips.
 * Originally
 * ListNodeItem's own renderSelectedDetail(), extracted so any other view
 * (currently TimelineView) can show the same read-only card for whichever
 * node the store has selected, without depending on ListView's own
 * ListNode/ListViewConfig/callbacks shape.
 */
import { DisplayNode } from '../types.js';
import { resolveNodeTitle } from '../DisplayGraph.js';
import { getNodeImageUrl as resolveNodeImageUrl, getDefaultImageUrl } from '../imageUtils.js';
import { unitCache } from '../../unit-cache.js';
import { renderStepTextsForNode, computeNodeTotalDuration, PreparationStep, RecipeTextContext } from '../RecipeTextRenderer.js';
import { durationBadgeHtml } from '../textFormatting.js';
import type { RecipeStore } from '../../store/types.js';

export interface NodeDetailCardOptions {
  /** Includes per-transformation preparation text and transformation/
   *  tool descriptions, same as ListNodeItem's own "children folded
   *  open" gate. Default false. */
  showPreparationText?: boolean;
  /** Shows the node's total elaboration time — this node and everything
   *  under it, via computeNodeTotalDuration(). Default true. TimelineView
   *  turns this off: it already shows each individual step's own
   *  duration (next to its preparation text, and in its header), so the
   *  cumulative whole-ingredient figure here would just be confusing
   *  alongside those. */
  showTotalDuration?: boolean;
  /** Scopes showPreparationText to just this one already-computed step
   *  (e.g. one entry of TimelineView's own this.boxes) instead of
   *  iterating every transformation the node has. A node with several
   *  transformations is really several independent steps — each with
   *  its own text/duration/description — so pass the specific step
   *  that's active rather than showing all of them lumped together.
   *  Also used as the title (the step's own label) instead of the
   *  node's generic title, matching the box's own label in the
   *  timeline. Ignored unless showPreparationText is also true. */
  activeStep?: PreparationStep;
}

/** Format a number, avoiding unnecessary trailing zeros. */
export function formatQuantityNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

export function buildNodeDetailCard(
  node: DisplayNode,
  store: RecipeStore,
  options: NodeDetailCardOptions = {},
): HTMLElement {
  const { showPreparationText = false, showTotalDuration = true, activeStep } = options;
  const data = node.data;
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    padding: 4px 16px 16px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    background: var(--accent-alpha-08);
  `;

  // Image (or fallback icon).
  const imageUrl = resolveNodeImageUrl(data) ?? null;
  const imageBox = document.createElement('div');
  imageBox.style.cssText = `
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${imageUrl ? 'transparent' : '#1a1a2e'};
  `;
  if (imageUrl) {
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = '';
    img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
    imageBox.appendChild(img);
  } else {
    const icon = document.createElement('img');
    icon.src = '/static/img/coberts.png';
    icon.alt = '';
    icon.style.cssText = 'width: 45px; height: 45px; object-fit: contain;';
    imageBox.appendChild(icon);
  }
  wrapper.appendChild(imageBox);

  // Title — full, never truncated.
  const title = document.createElement('div');
  title.style.cssText = 'font-size: 14px; color: #e0e0e0; font-family: inherit; text-align: center; display: flex; align-items: center; gap: 6px;';
  const titleText = document.createElement('span');
  titleText.textContent = activeStep ? activeStep.label : resolveNodeTitle(data);
  title.appendChild(titleText);
  wrapper.appendChild(title);

  // Node description, then ingredient description.
  if (data.description) {
    wrapper.appendChild(buildDetailNote(data.description));
  }
  if (data.ingredientInfo?.description) {
    wrapper.appendChild(buildDetailNote(data.ingredientInfo.description));
  }

  // Quantity (ingredient nodes only) — shown as stored, no scaling factor here.
  const quantity = data.ingredientInfo?.quantity;
  if (quantity) {
    const unitStr = unitCache.getDisplayName(quantity.unit, quantity.amount);
    const qtyEl = document.createElement('div');
    qtyEl.style.cssText = 'font-size: 12px; color: #a0a0a0; font-family: inherit;';
    qtyEl.textContent = `${formatQuantityNumber(quantity.amount)} ${unitStr}`;
    wrapper.appendChild(qtyEl);
  }

  // Computed total elaboration time for this node and everything under it.
  if (showTotalDuration) {
    const totalDurationSeconds = computeNodeTotalDuration(
      { store, scaleAmount: (amount) => amount },
      node,
    );
    if (totalDurationSeconds != null) {
      const durationEl = document.createElement('div');
      durationEl.style.cssText = 'display: flex; justify-content: center;';
      durationEl.innerHTML = durationBadgeHtml(totalDurationSeconds);
      wrapper.appendChild(durationEl);
    }
  }

  if (showPreparationText && activeStep) {
    // Scoped to this one step — a multi-transformation node is really
    // several independent steps, so only this step's own text/duration/
    // description show, not every transformation the node has. No right-
    // aligned duration badge here (unlike the "all transformations"
    // branch below) — the caller already shows this same step's own
    // duration prominently elsewhere (TimelineView's header), so
    // repeating it right next to the text would be redundant.
    if (activeStep.text != null) {
      const stepEl = document.createElement('div');
      stepEl.style.cssText = 'font-size: 13px; color: #e0e0e0; font-family: inherit; text-align: center; line-height: 1.5;';
      stepEl.innerHTML = activeStep.text;
      wrapper.appendChild(stepEl);
    }
    if (activeStep.trafoInfo?.description) wrapper.appendChild(buildDetailNote(activeStep.trafoInfo.description));
    // Tools aren't scoped per-transformation in the data model (a node's
    // toolInfos covers everything it uses "at once" — see ToolInfo's own
    // doc comment), so there's no narrower set to show here than the
    // node's full list, same as the chips below.
    for (const toolInfo of data.toolInfos ?? []) {
      if (toolInfo.description) wrapper.appendChild(buildDetailNote(toolInfo.description));
    }
  } else if (showPreparationText) {
    const ctx: RecipeTextContext = { store, scaleAmount: (amount) => amount };
    const stepTexts = renderStepTextsForNode(ctx, node);
    const transformationInfos = data.transformationInfos ?? [];
    stepTexts.forEach((text, i) => {
      const duration = transformationInfos[i]?.duration;
      const stepEl = document.createElement('div');
      if (duration) {
        stepEl.style.cssText = 'display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: 13px; color: #e0e0e0; font-family: inherit; line-height: 1.5;';
        stepEl.innerHTML = `<span style="flex: 1; min-width: 0;">${text}</span>${durationBadgeHtml(duration)}`;
      } else {
        stepEl.style.cssText = 'font-size: 13px; color: #e0e0e0; font-family: inherit; text-align: center; line-height: 1.5;';
        stepEl.innerHTML = text;
      }
      wrapper.appendChild(stepEl);
    });

    for (const transformationInfo of data.transformationInfos ?? []) {
      if (transformationInfo.description) wrapper.appendChild(buildDetailNote(transformationInfo.description));
    }
    for (const toolInfo of data.toolInfos ?? []) {
      if (toolInfo.description) wrapper.appendChild(buildDetailNote(toolInfo.description));
    }
  }

  // Tool chips — skipped for a bare tool node (its own title already names it).
  const toolInfos = data.toolInfos;
  const hasOwnIdentity = !!data.ingredientInfo?.ingredient || !!data.transformationInfos?.some(ti => ti.transformation);
  if (toolInfos && toolInfos.length > 0 && hasOwnIdentity) {
    const toolsEl = document.createElement('div');
    toolsEl.style.cssText = 'display: flex; flex-wrap: wrap; justify-content: center; gap: 4px;';
    for (const toolInfo of toolInfos) {
      const chip = document.createElement('span');
      chip.style.cssText = 'display: inline-flex; align-items: center; gap: 4px; font-size: 11px; padding: 1px 8px; border-radius: 9999px; border: 1px solid var(--accent-alpha-40); color: var(--accent); font-family: inherit;';
      const imgUrl = getDefaultImageUrl(toolInfo.tool.images);
      if (imgUrl) {
        const img = document.createElement('img');
        img.src = imgUrl;
        img.alt = '';
        img.style.cssText = 'width: 14px; height: 14px; border-radius: 50%; object-fit: cover; flex-shrink: 0;';
        img.addEventListener('error', () => { img.style.display = 'none'; });
        chip.appendChild(img);
      }
      chip.appendChild(document.createTextNode(toolInfo.fresh ? `${toolInfo.tool.name} ⟲` : toolInfo.tool.name));
      toolsEl.appendChild(chip);
    }
    wrapper.appendChild(toolsEl);
  }

  return wrapper;
}

function buildDetailNote(text: string): HTMLElement {
  const noteEl = document.createElement('div');
  noteEl.style.cssText = 'font-size: 12px; color: #888; font-family: inherit; text-align: center;';
  noteEl.textContent = text;
  return noteEl;
}
